import {
    LeftOutlined,
    LogoutOutlined,
} from '@ant-design/icons';
import { Layout, Menu, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import AdminProtectedRoute from './components/AdminProtectedRoute';
import AuthGuard from './components/AuthGuard';
import MobileDrawer from './components/mobile-drawer';
import MobileTabBar from './components/mobile-tab-bar';
import ProtectedRoute from './components/ProtectedRoute';
import {
  IconDashboard,
  IconProxies,
  IconProxyChecker,
  IconClients,
  IconConfigurations,
  IconNetworkPorts,
  IconConnections,
  IconSimManager,
  IconMarketing,
  IconSettings,
  IconSupport,
} from './components/sidebar-icons';
import Clients from './pages/Clients';
import Configurations from './pages/Configurations';
import Dashboard from './pages/Dashboard';
// import KrakenAdvancedEnhanced from './pages/KrakenAdvancedEnhanced';
import KrakenMulti from './pages/KrakenMulti';
import Login from './pages/Login';
import NetworkPorts from './pages/NetworkPorts';
import Proxies from './pages/Proxies';
import ProxyChecker from './pages/ProxyChecker';
import LocationCheck from './pages/LocationCheck';
import Marketing from './pages/Marketing';
import Settings from './pages/Settings';
import SettingsServices from './pages/SettingsServices';
import SupportInbox from './pages/SupportInbox';
import SimManager from './pages/SimManager';
import ConnectionsLayout from './pages/connections/ConnectionsLayout';
import ConnectionsRecovery from './pages/connections/ConnectionsRecovery';
import ConnectionsInstall from './pages/connections/ConnectionsInstall';
import ConnectionsJournalS3 from './pages/connections/ConnectionsJournalS3';
import JournalS3Exports from './pages/journal-s3/JournalS3Exports';
import JournalS3Layout from './pages/journal-s3/JournalS3Layout';
import JournalS3Search from './pages/journal-s3/JournalS3Search';
import { BulkUpdateBannerHost } from './context/BulkUpdateContext';
import { KRAKEN_STATUS, KRAKEN_STATUS_LABEL, useKrakenStatus } from './hooks/useKrakenStatus';
import { apiService } from './services/api';
import { attachSpaMiddleClickLinks, isPrimarySpaNavigation } from './utils/spaLinkUtils';
import './styles/sidebar.css';
import tokenManager from './utils/tokenManager';
import {
  isSettingsPath,
  SETTINGS_CLIENT_CABINET_PATH,
  SETTINGS_LOGS_PATH,
  SETTINGS_MENU_KEY,
  SETTINGS_PROXY_MANAGER_PATH,
  SETTINGS_SERVICES_PATH,
  SETTINGS_BACKUPS_PATH,
  SETTINGS_SUPPORT_PATH,
} from './config/settings-routes';
import { CONNECTIONS_BASE_PATH, isConnectionsPath } from './config/connections-routes';
import { JOURNAL_S3_PATH, isJournalS3Path } from './config/journal-s3-routes';

const { Sider, Content } = Layout;

function App() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const krakenStatus = useKrakenStatus(location.pathname !== '/login');
  const [sidebarOpenKeys, setSidebarOpenKeys] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
     setMobileMenuOpen(false);
  }, [location.pathname]);

  const CHECKS_MENU_KEY = 'checks';

  useEffect(() => {
    if (isSettingsPath(location.pathname)) {
      setSidebarOpenKeys((previousKeys) => (
        previousKeys.includes(SETTINGS_MENU_KEY) ? previousKeys : [SETTINGS_MENU_KEY]
      ));
    }
    if (location.pathname === '/proxy-checker' || location.pathname === '/location-check') {
      setSidebarOpenKeys((previousKeys) => (
        previousKeys.includes(CHECKS_MENU_KEY) ? previousKeys : [...previousKeys, CHECKS_MENU_KEY]
      ));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/login') {
      // Инициализируем менеджер токенов
      tokenManager.init();
      checkUserRole();
      // Не блокируем рендер всего приложения статусом Kraken:
      // при медленном API этот запрос может идти долго.
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isAdmin && !isManager) {
      setSupportUnread(0);
      return undefined;
    }
    let stopped = false;
    const tick = () => {
      apiService.get('/api/v1/admin/support/unread-count')
        .then((response) => {
          if (!stopped) setSupportUnread(Number(response.data?.count) || 0);
        })
        .catch(() => {});
    };
    tick();
    const timer = window.setInterval(tick, 30000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [isAdmin, isManager]);

// ИСПРАВЛЕНО: Оптимизирован обработчик событий с debounce
useEffect(() => {
  let updateTimeout = null;
  
  const handleUserUpdate = () => {
    // ИСПРАВЛЕНО: Debounce для предотвращения множественных вызовов
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    updateTimeout = setTimeout(() => {
      checkUserRole();
    }, 50);
  };

  window.addEventListener('user-updated', handleUserUpdate, { passive: true });
  window.addEventListener('user-cleared', handleUserUpdate, { passive: true });

  return () => {
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    window.removeEventListener('user-updated', handleUserUpdate);
    window.removeEventListener('user-cleared', handleUserUpdate);
  };
}, []);

  const checkUserRole = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        const role = typeof user.role === 'string' ? user.role.toLowerCase() : null;
        setIsAdmin(role === 'admin');
        setIsManager(role === 'manager');
        return;
      }
      
      // Пробуем получить роль из токена
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAdmin(false);
        setIsManager(false);
        return;
      }
      
      // Проверяем demo-token
      if (token === 'demo-token') {
        setIsAdmin(true);
        setIsManager(false);
        return;
      }
      
      // Проверяем demo-jwt- токены
      if (token.startsWith('demo-jwt-')) {
        try {
          const base64Part = token.replace('demo-jwt-', '');
          const decoded = JSON.parse(atob(base64Part));
          const decodedRole = typeof decoded.role === 'string' ? decoded.role.toLowerCase() : null;
          setIsAdmin(decodedRole === 'admin');
          setIsManager(decodedRole === 'manager');
        } catch (e) {
          setIsAdmin(false);
          setIsManager(false);
        }
        return;
      }
      
      // Для обычных JWT токенов пытаемся получить информацию через API
      setIsAdmin(false);
      setIsManager(false);
    } catch (error) {
      setIsAdmin(false);
      setIsManager(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
      message.success('Вы вышли из системы');
    } catch (error) {
      message.warning('Ошибка при выходе, выполняется принудительный выход');
    } finally {
      // Используем tokenManager для корректного выхода
      tokenManager.logout();
    }
  };

  useEffect(() => {
    return attachSpaMiddleClickLinks();
  }, []);

  const handleSidebarAnchorClick = useCallback((event, path) => {
    if (!isPrimarySpaNavigation(event)) {
      return;
    }

    event.preventDefault();
    navigate(path);
  }, [navigate]);

  const handleSidebarMenuClick = useCallback(({ key, domEvent }) => {
    if (domEvent.defaultPrevented) {
      return;
    }
    if (!isPrimarySpaNavigation(domEvent)) {
      return;
    }

    navigate(key);
  }, [navigate]);

  const buildMenuItem = (path, icon, text, extra = {}) => ({
    key: path,
    ...(icon ? { icon: <span data-sidebar-path={path}>{icon}</span> } : {}),
    label: (
      <a
        href={path}
        className="sidebar-menu-link"
        data-sidebar-path={path}
        onClick={(event) => handleSidebarAnchorClick(event, path)}
      >
        {text}
        {path === SETTINGS_SUPPORT_PATH && supportUnread > 0 ? (
          <span className="sidebar-menu-count">{supportUnread > 99 ? '99+' : supportUnread}</span>
        ) : null}
      </a>
    ),
    ...extra,
  });

  const menuItems = [
    buildMenuItem('/', <IconDashboard />, 'Дашборд'),
    buildMenuItem('/proxies', <IconProxies />, 'Прокси'),
    {
      key: CHECKS_MENU_KEY,
      icon: <span data-sidebar-path={CHECKS_MENU_KEY}><IconProxyChecker /></span>,
      label: 'Проверки',
      children: [
        buildMenuItem('/proxy-checker', null, 'Проверка прокси'),
        buildMenuItem('/location-check', null, 'Проверка локаций'),
      ],
    },
    buildMenuItem('/clients', <IconClients />, 'Клиенты'),
    buildMenuItem('/configurations', <IconConfigurations />, 'Конфигурации'),
    buildMenuItem('/network-ports', <IconNetworkPorts />, 'Сетевые порты'),
    buildMenuItem('/kraken-multi', <IconConnections />, 'Подключения', { danger: krakenStatus === KRAKEN_STATUS.disconnected }),
    buildMenuItem('/sim-manager', <IconSimManager />, 'SIM-менеджер'),
  ];

  if (isAdmin || isManager) {
    menuItems.push(buildMenuItem(SETTINGS_SUPPORT_PATH, <IconSupport />, 'Поддержка'));
  }

  // Пункты «Маркетинг» и «Настройки» — только для администраторов
  if (isAdmin) {
    menuItems.push(
      buildMenuItem(JOURNAL_S3_PATH, <IconConfigurations />, 'Журналы S3'),
      buildMenuItem('/marketing', <IconMarketing />, 'Маркетинг'),
      {
        key: SETTINGS_MENU_KEY,
        icon: <span data-sidebar-path={SETTINGS_MENU_KEY}><IconSettings /></span>,
        label: 'Настройки',
        children: [
          buildMenuItem(SETTINGS_PROXY_MANAGER_PATH, null, 'Proxy Manager'),
          buildMenuItem(SETTINGS_CLIENT_CABINET_PATH, null, 'Кабинет клиента'),
          buildMenuItem(SETTINGS_LOGS_PATH, null, 'Логи'),
          buildMenuItem(SETTINGS_SERVICES_PATH, null, 'Службы'),
          buildMenuItem(SETTINGS_BACKUPS_PATH, null, 'Бэкапы'),
        ],
      },
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader-container">
          <div className="loader-ball" />
          <div className="loader-shadow" />
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={
        <AuthGuard>
      <Layout style={{ minHeight: '100vh' }}>
        {!isMobile && (
          <Sider 
            collapsible 
            collapsed={collapsed} 
            onCollapse={setCollapsed}
            trigger={null}
            theme="dark"
            className="app-sider"
            width={240}
            collapsedWidth={64}
            style={{
              position: 'fixed',
              height: '100vh',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 1000,
              overflow: 'auto'
            }}
          >
            <div className="sidebar-statistics-label">Статистика</div>
            <div className="sidebar-logo">
              <img 
                src={collapsed ? "/3favicon.svg" : "/logo-new.svg"} 
                alt="LTE.Center Logo" 
                className={collapsed ? 'collapsed' : ''}
                fetchpriority="high"
              />
            </div>
            <nav className="sidebar-nav">
              <Menu
                className="sidebar-menu"
                mode="inline"
                selectedKeys={[
                  isSettingsPath(location.pathname)
                    ? location.pathname
                    : isConnectionsPath(location.pathname)
                      ? CONNECTIONS_BASE_PATH
                      : isJournalS3Path(location.pathname)
                        ? JOURNAL_S3_PATH
                        : location.pathname
                ]}
                openKeys={sidebarOpenKeys}
                onOpenChange={setSidebarOpenKeys}
                items={menuItems}
                onClick={handleSidebarMenuClick}
                style={{ borderRight: 0 }}
              />
            </nav>
            <div className="sidebar-status-block">
              <div className="sidebar-status-row" role="status" aria-label="Статус подключения">
                <div
                  className={krakenStatus === KRAKEN_STATUS.connected
                    ? 'sidebar-status-dot'
                    : `sidebar-status-dot ${krakenStatus}`}
                />
                <span className="sidebar-status-text">
                  {KRAKEN_STATUS_LABEL[krakenStatus]}
                </span>
              </div>
            </div>
            <div className="sidebar-bottom-menu">
              <Menu
                mode="inline"
                selectedKeys={[]}
                items={[
                  {
                    key: 'collapse',
                    icon: <LeftOutlined style={{ transform: collapsed ? 'rotate(180deg)' : undefined }} />,
                    label: 'Свернуть',
                    onClick: () => setCollapsed(!collapsed)
                  },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Выйти',
                    onClick: handleLogout
                  }
                ]}
                style={{ borderRight: 0 }}
              />
            </div>
          </Sider>
        )}

        {isMobile && (
          <MobileDrawer
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            onLogout={handleLogout}
            krakenStatus={krakenStatus}
            isAdmin={isAdmin}
            isManager={isManager}
            supportUnread={supportUnread}
          />
        )}
        
        <Layout 
          className={`site-layout-content ${collapsed && !isMobile ? 'site-layout-content-collapsed' : ''}`}
          style={{ 
            transition: 'margin-left 0.2s',
            marginLeft: isMobile ? 0 : (collapsed ? 64 : 240)
          }}
        >
          {isMobile && (
            <header className="mobile-header">
              <div className="mobile-header__logo-wrap">
                <img
                  src="/logo-new.svg"
                  alt="LTE.Center Logo"
                  className="mobile-header__logo"
                  fetchpriority="high"
                />
              </div>
              <button
                type="button"
                className="mobile-header__status"
                aria-label={KRAKEN_STATUS_LABEL[krakenStatus]}
                tabIndex={0}
              >
                <span
                  className={krakenStatus === KRAKEN_STATUS.connected
                    ? 'mobile-header__status-dot'
                    : `mobile-header__status-dot mobile-header__status-dot--${krakenStatus}`}
                />
              </button>
              <button
                type="button"
                className="mobile-header__burger"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Открыть меню"
                tabIndex={0}
              >
                <svg className="mobile-header__burger-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="0" y="4" width="24" height="2" rx="1" fill="#fff" />
                  <rect x="0" y="11" width="24" height="2" rx="1" fill="#fff" />
                  <rect x="0" y="18" width="24" height="2" rx="1" fill="#fff" />
                </svg>
              </button>
            </header>
          )}
          <Content className="ant-layout-content">
            <BulkUpdateBannerHost />
            <Routes>
              <Route path="/" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/clients" element={
                <ProtectedRoute>
                  <Clients />
                </ProtectedRoute>
              } />
              <Route path="/proxies" element={
                <ProtectedRoute>
                  <Proxies />
                </ProtectedRoute>
              } />
              <Route path="/configurations" element={
                <ProtectedRoute>
                  <Configurations />
                </ProtectedRoute>
              } />
              {/* <Route path="/hybrid-sync" element={
                <ProtectedRoute>
                  <HybridSync />
                </ProtectedRoute>
              } /> */}
              <Route path="/kraken-multi" element={
                <ProtectedRoute>
                  <ConnectionsLayout />
                </ProtectedRoute>
              }>
                <Route index element={<KrakenMulti showTitle={false} />} />
                <Route path="recovery" element={<ConnectionsRecovery />} />
                <Route path="install" element={<ConnectionsInstall />} />
                <Route path="journal-s3" element={<ConnectionsJournalS3 />} />
              </Route>
              <Route path="/network-ports" element={
                <ProtectedRoute>
                  <NetworkPorts />
                </ProtectedRoute>
              } />
              {/* <Route path="/kraken-advanced" element={
                <ProtectedRoute>
                  <KrakenAdvancedEnhanced />
                </ProtectedRoute>
              } /> */}
              <Route path="/proxy-checker" element={
                <ProtectedRoute>
                  <ProxyChecker />
                </ProtectedRoute>
              } />
              <Route path="/location-check" element={
                <ProtectedRoute>
                  <LocationCheck />
                </ProtectedRoute>
              } />
              <Route path="/sim-manager" element={
                <ProtectedRoute>
                  <SimManager />
                </ProtectedRoute>
              } />
              <Route path="/marketing" element={
                <AdminProtectedRoute>
                  <Marketing />
                </AdminProtectedRoute>
              } />
              <Route path="/journal-s3" element={
                <AdminProtectedRoute>
                  <JournalS3Layout />
                </AdminProtectedRoute>
              }>
                <Route index element={<JournalS3Exports />} />
                <Route path="search" element={<JournalS3Search />} />
              </Route>
              <Route path="/settings" element={
                <AdminProtectedRoute>
                  <Navigate to={SETTINGS_PROXY_MANAGER_PATH} replace />
                </AdminProtectedRoute>
              } />
              <Route path={SETTINGS_PROXY_MANAGER_PATH} element={
                <AdminProtectedRoute>
                  <Settings />
                </AdminProtectedRoute>
              } />
              <Route path={SETTINGS_CLIENT_CABINET_PATH} element={
                <AdminProtectedRoute>
                  <Settings />
                </AdminProtectedRoute>
              } />
              <Route path={SETTINGS_LOGS_PATH} element={
                <AdminProtectedRoute>
                  <Settings />
                </AdminProtectedRoute>
              } />
              <Route path={SETTINGS_SERVICES_PATH} element={
                <AdminProtectedRoute>
                  <SettingsServices />
                </AdminProtectedRoute>
              } />
              <Route path={SETTINGS_BACKUPS_PATH} element={
                <AdminProtectedRoute>
                  <Settings />
                </AdminProtectedRoute>
              } />
              <Route path={SETTINGS_SUPPORT_PATH} element={
                <ProtectedRoute>
                  <SupportInbox isAdmin={isAdmin} />
                </ProtectedRoute>
              } />
            </Routes>
          </Content>
          {isMobile && <MobileTabBar isAdmin={isAdmin} />}
        </Layout>
      </Layout>
        </AuthGuard>
      } />
    </Routes>
  );
}

export default App;
